import { PlusIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useEffect, useState } from 'react';
import { createClient, deleteClientById, getClients, updateClient } from '../clients/clientClient';
import { Client } from '../common/types';
import AddClientModal from '../components/AddClientModal';
import ClientListItem from '../components/ClientListItem';
import DeleteClientModal from '../components/DeleteClientModal';
import EditClientModal from '../components/EditClientModal';
import EmptyState from '../components/EmptyState';
import ErrorBanner from '../components/ErrorBanner';
import LimitedReachedUpgradeModal from '../components/LimitReachedUpgradeModal';
import LoadingState from '../components/LoadingState';
import ProductTourWrapper from '../components/ProductTourWrapper';
import Search from '../components/Search';
import Sidebar from '../components/Sidebar';
import UpgradeModal from '../components/UpgradeModal';
import { SubscriptionTier, tierLimits } from '../context/FeatureGate/FeatureGate';
import { useFeatureGate } from '../context/FeatureGate/FeatureGateContext';
import { useGlobalContext } from '../context/GlobalProvider';
import { useProductTourContext } from '../context/ProductTourContext';
import { clientsPageStepIndex } from '../data/productTourSteps';
import { useSearch } from '../hooks/useSearch';
import RecordAudioButton from '../RecordAudioButton';
import ButtonPlaceholder from './ButtonPlaceholder';

const renderAddClientButton = (isDisabled: boolean, onClick: () => void): ReactNode => {
  return (
    <button
      disabled={isDisabled}
      id="add-client-button"
      onClick={onClick}
      type="button"
      className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-color duration-300"
    >
      <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
      Add Client
    </button>
  );
};

export default function ClientsPage() {
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isDeleteClientModalOpen, setIsDeleteClientModalOpen] = useState(false);
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isLimitReachedModalOpen, setIsLimitReachedModalOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);

  const { featureGate } = useFeatureGate();
  const shouldShowUpgradeButton = featureGate && !featureGate.isPaidTier();

  const clearForm = () => {
    setFirstName('');
    setLastName('');
  };

  const queryClient = useQueryClient();
  const { setState } = useGlobalContext();

  // Fetch all clients for current user
  const {
    data: clients,
    error: clientsError,
    isLoading: isClientsLoading
  } = useQuery({
    queryKey: ['getClients'],
    queryFn: getClients,
    select: (clients) => [...clients].sort((a, b) => a.name.localeCompare(b.name))
  });

  const { filteredItems: filteredClients, ...searchControls } = useSearch({ items: clients });

  // ============================= PRODUCT TOUR ===============================
  const { setState: setTourState, state: tourState } = useProductTourContext();

  const pauseTour = () => setTourState({ ...tourState, run: false });
  const resumeTour = () => setTourState({ ...tourState, run: true });

  // On mount, if tour is active, trigger the first step for the clients page
  useEffect(() => {
    if (tourState.tourActive) {
      pauseTour();
      const timer = setTimeout(() => resumeTour(), 600); // ms delay to ensure the tour is active before setting the step index.
      return () => clearTimeout(timer);
    }
  }, []);

  // Update modal open state based on tour state
  useEffect(() => {
    if (tourState.tourActive && tourState.run) {
      setIsAddClientModalOpen(tourState.shouldModalOpen);
    }
  }, [tourState.shouldModalOpen]);

  const moveToNextTourStep = (currentStepIndex: number) => {
    if (tourState.tourActive && tourState.stepIndex === currentStepIndex) {
      setTourState({ ...tourState, stepIndex: currentStepIndex + 1 });
    }
  };
  // ========================= END PRODUCT TOUR ===============================

  // Create meeting mutation that is triggered on add client form submission
  const createClientMutation = useMutation({
    mutationFn: ({
      name,
      firstName,
      lastName
    }: {
      name: string;
      firstName: string;
      lastName: string;
    }) => {
      return createClient(name, firstName, lastName);
    },
    onSuccess: () => {
      // Refetch clients to display new client
      queryClient.invalidateQueries({ queryKey: ['getClients'] });

      // Close modal on success
      setIsAddClientModalOpen(false);

      // Toast notification
      setState((prevState) => ({
        ...prevState,
        clientCreationSuccess: true
      }));
    },
    onError: () => {
      // Toast notification
      setState((prevState) => ({
        ...prevState,
        clientCreationError: true
      }));
    }
  });

  const updateClientMutation = useMutation({
    mutationFn: (client: Client) => {
      return updateClient(client.id, client.firstName, client.lastName);
    },
    onSuccess: (updatedClient: Client) => {
      // Refetch clients to display updated client
      // queryClient.invalidateQueries({ queryKey: ['getClients'] });

      // Manually update the client in the cache. This is more efficient than refetching and
      // also is a temporary fix to gets around the issue of client ordering
      queryClient.setQueryData(['getClients'], (oldClients: Client[] | undefined) => {
        if (!oldClients) return oldClients;
        return oldClients.map((client) =>
          client.id === clientToEdit?.id ? updatedClient : client
        );
      });

      // Close modal on success
      setIsEditClientModalOpen(false);
      setClientToEdit(null);

      // Toast notification
      setState((prevState) => ({
        ...prevState,
        clientUpdateSuccess: true
      }));
    },
    onError: () => {
      // Toast notification
      setState((prevState) => ({
        ...prevState,
        clientUpdateError: true
      }));
    }
  });

  const deleteClientMutation = useMutation({
    mutationFn: (clientId: number) => {
      return deleteClientById(clientId);
    },
    onMutate: (clientId: number) => {
      // Optimistically update the UI
      queryClient.setQueryData(['getClients'], (oldClients: Client[] | undefined) => {
        if (!oldClients) return oldClients;
        return oldClients.filter((client) => client.id !== clientId);
      });
    },
    onSuccess: () => {
      // Refetch clients to display updated list of clients
      queryClient.invalidateQueries({ queryKey: ['getClients'] });
      // Toast notification
      setState((prevState) => ({
        ...prevState,
        clientDeletionSuccess: true
      }));
    },
    onError: () => {
      // Invalidate the query to refetch the data
      queryClient.invalidateQueries({ queryKey: ['getClients'] });

      // Toast notification
      setState((prevState) => ({
        ...prevState,
        clientDeletionError: true
      }));
    }
  });

  const trimNames = (firstName: string, lastName: string) => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();

    return {
      trimmedFirstName,
      trimmedLastName,
      name: `${trimmedFirstName} ${trimmedLastName}`
    };
  };

  /**
   * On form submission,
   * 1. prevent window from refreshing
   * 2. trigger create meeting mutation with data from form
   */
  const submitClient = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Trim names to remove leading and trailing spaces
    const { trimmedFirstName, trimmedLastName, name } = trimNames(firstName, lastName);

    // If either name is empty, set isSubmitting to false and return
    if (!trimmedFirstName || !trimmedLastName) {
      setIsSubmitting(false);
      return;
    }

    createClientMutation.mutate(
      { name, firstName: trimmedFirstName, lastName: trimmedLastName },
      {
        onSuccess: () => {
          clearForm();
          setTimeout(() => {
            setIsSubmitting(false);
          }, 1000);
        },
        onError: () => {
          setIsSubmitting(false);
        }
      }
    );
  };

  const handleAddClientClick = () => {
    // Handle tour, if it is active
    moveToNextTourStep(clientsPageStepIndex);
    // If user's subscription tier allows them to create more clients, open add client modal
    if (featureGate?.canCreateClient(clients!.length)) setIsAddClientModalOpen(true);
    // Otherwise, open the limit reached upgrade modal
    else setIsLimitReachedModalOpen(true);
  };

  // When user confirms client deletion via delete client modal,
  // trigger delete client mutation, close modal, and reset client to delete
  const handleClientDeletion = () => {
    if (!clientToDelete) return;
    deleteClientMutation.mutate(clientToDelete.id);
    setIsDeleteClientModalOpen(false);
    setClientToDelete(null);
  };

  const handleClientEdit = () => {
    if (!clientToEdit) return;
    updateClientMutation.mutate(clientToEdit);
  };

  if (isClientsLoading)
    return (
      <>
        <Sidebar currentPageName={'Clients'} />
        <main className="lg:pl-72 flex items-center">
          <LoadingState />
        </main>
      </>
    );

  const handleCloseAddClientModal = () => {
    setIsAddClientModalOpen(false);
    createClientMutation.reset();
  };

  const handleAddCloseEditClientModal = () => {
    setIsEditClientModalOpen(false);
    updateClientMutation.reset();
  };

  return (
    <ProductTourWrapper>
      <Sidebar currentPageName={'Clients'} />
      <AddClientModal
        isOpen={isAddClientModalOpen}
        onClose={handleCloseAddClientModal}
        firstName={firstName}
        lastName={lastName}
        setFirstName={setFirstName}
        setLastName={setLastName}
        submitClient={submitClient}
        isSubmitting={isSubmitting}
      />

      <main className="lg:ml-72">
        <div className="relative w-full">
          {/* Upgrade Button*/}
          {shouldShowUpgradeButton && (
            <button
              onClick={() => setIsUpgradeModalOpen(true)}
              className="absolute flex items-center top-6 right-6 z-50 
                 hover:bg-teal-400 text-sm font-semibold text-white
                 px-3.5 py-2 rounded-lg shadow-lg bg-teal-500
                 transition duration-300"
            >
              <p>Upgrade</p>
            </button>
          )}
        </div>

        <div className="px-4 py-10 sm:px-6 lg:px-8 lg:py-6 mb-12">
          {/* Heading */}
          <div className="border-b border-gray-200 pt-4 pb-6 text-gray-800">
            <h3 className="text-3xl font-bold">Your Clients</h3>
          </div>

          {/* If error loading clients */}
          {clientsError && <ErrorBanner message={clientsError.message} />}

          {/* Client List */}
          {clients?.length ? (
            <>
              <div className="flex flex-col md:flex-row justify-between gap-8 mt-8 xl:w-3/4 mb-6">
                <div>
                  {featureGate ? (
                    renderAddClientButton(!featureGate, handleAddClientClick)
                  ) : (
                    <ButtonPlaceholder />
                  )}
                </div>

                <Search
                  {...searchControls}
                  itemName={'client'}
                  // onSearchResultClick={(result) => goToClientPage(result.id)}
                  // renderSearchResult={renderClientSearchResult}
                />
              </div>

              <ul id="clients-list" role="list" className="divide-y divide-gray-100 mt-4 xl:w-3/4">
                {filteredClients.length > 0 ? (
                  filteredClients.map((client) => (
                    <ClientListItem
                      key={client.id}
                      client={client}
                      handleDeletionRequest={() => {
                        setClientToDelete(client);
                        setIsDeleteClientModalOpen(true);
                      }}
                      handleEditRequest={() => {
                        setClientToEdit(client);
                        setIsEditClientModalOpen(true);
                      }}
                    />
                  ))
                ) : (
                  <div className="mt-12">
                    <span className="text-gray-700">
                      No clients match your search query, "
                      <span className="font-bold">{searchControls.searchTerm}</span>"
                    </span>
                  </div>
                )}
              </ul>
            </>
          ) : (
            // Empty State
            <EmptyState
              title="No Clients"
              description="Get started by creating your first client!"
              icon={UserCircleIcon}
              buttonText="Create Client"
              onClick={() => setIsAddClientModalOpen(true)}
            />
          )}
        </div>
        <RecordAudioButton />
      </main>
      <DeleteClientModal
        isOpen={isDeleteClientModalOpen}
        setIsOpen={setIsDeleteClientModalOpen}
        client={clientToDelete}
        handleDeleteClient={handleClientDeletion}
      />
      <EditClientModal
        client={clientToEdit}
        setClient={setClientToEdit}
        submitClient={handleClientEdit}
        isOpen={isEditClientModalOpen}
        setIsOpen={setIsEditClientModalOpen}
        isSubmitting={updateClientMutation.isPending}
        error={updateClientMutation.error?.message}
        onClose={handleAddCloseEditClientModal}
      />
      <UpgradeModal isOpen={isUpgradeModalOpen} setIsOpen={setIsUpgradeModalOpen} />
      <LimitedReachedUpgradeModal
        limitResource={'clients'}
        limit={featureGate?.clientLimit || tierLimits[SubscriptionTier.FREE].maxClients}
        tier={featureGate?.userTier || SubscriptionTier.FREE}
        isOpen={isLimitReachedModalOpen}
        setIsOpen={setIsLimitReachedModalOpen}
      />
    </ProductTourWrapper>
  );
}
